/**
 * สัญญาจ้าง / สัญญาทดลองงาน — หน้ารายการฝั่ง HR
 *
 * ฟอร์มออกสัญญาแยกเป็นหน้าเต็มของตัวเอง (/hr/contracts/new) ตามมาตรฐานบ้านนี้:
 * ฟอร์มกรอกข้อมูลต้องมี URL ของตัวเอง ไม่ยัดใน modal — modal เหลือไว้แค่กล่อง
 * ยืนยันปุ่มเดียว (ที่นี่คือกล่อง "ยกเลิกสัญญา" ซึ่งต้องพิมพ์เหตุผล)
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileSignature, Plus, Send, XCircle, Download, ShieldCheck } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadError } from '../../components/layout/LoadError';
import { Loading } from '../../components/layout/Loading';
import { EmptyState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import {
  useContracts, useSendContract, useCancelContract,
  type ContractRow, type ContractStatus,
} from '../../hooks';
import { apiDownload } from '../../lib/api-client';

const STATUS: Record<ContractStatus, { label: string; cls: string }> = {
  draft: { label: 'ร่าง', cls: 'bg-slate-100 text-slate-700' },
  sent: { label: 'รอลงนาม', cls: 'bg-amber-100 text-amber-800' },
  viewed: { label: 'เปิดอ่านแล้ว', cls: 'bg-blue-100 text-blue-700' },
  signed: { label: 'ลงนามแล้ว', cls: 'bg-green-100 text-green-700' },
  cancelled: { label: 'ยกเลิก', cls: 'bg-gray-100 text-gray-500' },
};

const KIND: Record<ContractRow['contract_kind'], string> = {
  permanent: 'ไม่มีกำหนดเวลา',
  fixed_term: 'มีกำหนดเวลา',
  probation: 'ทดลองงาน',
};

function thaiDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('th-TH', { timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: '2-digit' });
}

export function HRContracts() {
  const [statusFilter, setStatusFilter] = useState<ContractStatus | ''>('');
  const { data, isLoading, error, refetch } = useContracts(
    statusFilter ? { status: statusFilter } : undefined,
  );
  const send = useSendContract();
  const cancel = useCancelContract();
  const toast = useToast();
  const consequence = useConsequence();
  const navigate = useNavigate();

  const [cancelTarget, setCancelTarget] = useState<ContractRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  async function handleSend(row: ContractRow) {
    const ok = await consequence({
      title: `ส่งสัญญา ${row.doc_no} ให้ ${row.name}`,
      intro: 'ระบบจะส่งการ์ดเข้าไลน์ให้พนักงานอ่านและลงนาม',
      lines: [
        { label: 'พนักงาน', value: `${row.name} (${row.emp_code})` },
        { label: 'ประเภทสัญญา', value: KIND[row.contract_kind] },
      ],
      effects: [
        'สัญญาจะถูกล็อก แก้ไขไม่ได้อีก — ถ้าออกผิดต้องยกเลิกแล้วออกฉบับใหม่',
        'พนักงานจะได้รับการ์ดในไลน์ทันที',
      ],
      // เตือนตรงจังหวะที่ยังถอยได้ — พอส่งไปแล้วแก้ไม่ได้อีก
      alerts: (row.missing_fields ?? []).length > 0
        ? [{
            level: 'warn' as const,
            title: 'ประวัติพนักงานไม่ครบ — สัญญาจะเว้นว่าง',
            detail: `${(row.missing_fields ?? []).join(' · ')} · สัญญาที่ไม่มีเลขบัตรประชาชนใช้ระบุตัวคู่สัญญาไม่ได้เต็มที่`,
          }]
        : [],
      confirmText: 'ส่งให้ลงนาม',
    });
    if (!ok) return;
    try {
      const res = await send.mutateAsync(row.id);
      // ⭐ ต้องบอกตรง ๆ ถ้าส่งไลน์ไม่ถึง ไม่งั้น HR นั่งรอสัญญาที่ไม่มีวันถูกเซ็น
      if (res.line_pushed) toast.success(res.message);
      else toast.info(res.message);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ส่งสัญญาไม่สำเร็จ');
    }
  }

  async function handleCancel() {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) { toast.error('กรุณาระบุเหตุผลที่ยกเลิก'); return; }
    try {
      const res = await cancel.mutateAsync({ id: cancelTarget.id, reason: cancelReason.trim() });
      toast.success(res.message);
      setCancelTarget(null);
      setCancelReason('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ยกเลิกไม่สำเร็จ');
    }
  }

  async function download(row: ContractRow) {
    try {
      await apiDownload(`/hr/contracts/${encodeURIComponent(row.id)}/pdf`, `สัญญาจ้าง-${row.doc_no.replace('/', '-')}.pdf`);
    } catch (e) {
      toast.error(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    }
  }

  return (
    <AdminLayout title="สัญญาจ้าง">
      <div className="space-y-4 max-w-5xl">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[12rem]">
            <label className="label">สถานะ</label>
            <select
              className="input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ContractStatus | '')}
            >
              <option value="">ทั้งหมด</option>
              <option value="draft">ร่าง</option>
              <option value="sent">รอลงนาม</option>
              <option value="viewed">เปิดอ่านแล้ว</option>
              <option value="signed">ลงนามแล้ว</option>
              <option value="cancelled">ยกเลิก</option>
            </select>
          </div>
          <Link to="/hr/contracts/new" className="btn-cta">
            <Plus className="w-4 h-4" /> ออกสัญญาใหม่
          </Link>
        </div>

        {isLoading ? (
          <Loading />
        ) : error || !data ? (
          <LoadError error={error} onRetry={() => void refetch()} />
        ) : data.unavailable ? (
          <div className="card p-4 border-l-4 border-l-amber-400 text-sm">{data.unavailable}</div>
        ) : data.contracts.length === 0 ? (
          <EmptyState
            icon={FileSignature}
            title="ยังไม่มีสัญญาในระบบ"
            detail="ออกสัญญาจ้างหรือสัญญาทดลองงานให้พนักงาน แล้วส่งเข้าไลน์ให้เซ็นได้เลย"
            action={{ label: 'ออกสัญญาใหม่', onClick: () => navigate('/hr/contracts/new') }}
          />
        ) : (
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">เลขที่</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">พนักงาน</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">ประเภท</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">เริ่ม</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">สถานะ</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {data.contracts.map((row) => {
                  const st = STATUS[row.status];
                  return (
                    <tr key={row.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{row.doc_no}</td>
                      <td className="px-3 py-2">
                        <div>{row.name}</div>
                        <div className="text-xs text-gray-500">{row.emp_code}</div>
                        {(row.missing_fields ?? []).length > 0 && row.status !== 'cancelled' && (
                          <div className="text-xs text-amber-700 mt-0.5">
                            ข้อมูลไม่ครบ: {(row.missing_fields ?? []).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{KIND[row.contract_kind]}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{thaiDate(row.start_date)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${st.cls}`}>
                          {st.label}
                        </span>
                        {row.status === 'signed' && (
                          <span className="ml-1 inline-flex items-center text-xs text-green-700 whitespace-nowrap">
                            <ShieldCheck className="w-3.5 h-3.5 mr-0.5" />{thaiDate(row.signed_at)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          {(row.status === 'draft' || row.status === 'sent' || row.status === 'viewed') && (
                            <button
                              className="btn-secondary btn-slim"
                              onClick={() => void handleSend(row)}
                              disabled={send.isPending}
                            >
                              <Send className="w-3.5 h-3.5" />
                              {row.status === 'draft' ? 'ส่งให้ลงนาม' : 'ส่งซ้ำ'}
                            </button>
                          )}
                          <button className="btn-secondary btn-slim" onClick={() => void download(row)}>
                            <Download className="w-3.5 h-3.5" /> PDF
                          </button>
                          {row.status !== 'cancelled' && (
                            <button
                              className="btn-secondary btn-slim text-red-700 border-red-200"
                              onClick={() => { setCancelTarget(row); setCancelReason(''); }}
                            >
                              <XCircle className="w-3.5 h-3.5" /> ยกเลิก
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* กล่องยืนยันปุ่มเดียว + ช่องเหตุผล — สัญญาลบไม่ได้ ยกเลิกต้องมีเหตุผลเสมอ */}
      <Modal
        open={!!cancelTarget}
        onClose={() => { if (!cancel.isPending) { setCancelTarget(null); setCancelReason(''); } }}
        title={`ยกเลิกสัญญา ${cancelTarget?.doc_no ?? ''}`}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            สัญญาลบออกจากระบบไม่ได้ — การยกเลิกจะทำให้ฉบับนี้เป็นโมฆะและเก็บไว้เป็นหลักฐาน
            {cancelTarget?.status === 'signed' && (
              <span className="block mt-1 text-red-700">
                ฉบับนี้ลงนามไปแล้ว พนักงานยังถือไฟล์เดิมอยู่ในมือ — ต้องแจ้งพนักงานและออกฉบับใหม่แทน
              </span>
            )}
          </p>
          <div>
            <label className="label">เหตุผลที่ยกเลิก</label>
            <textarea
              className="input"
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="เช่น กรอกอัตราค่าจ้างผิด ออกฉบับใหม่แทน"
            />
          </div>
          <div className="flex gap-2">
            <button
              className="btn-secondary flex-1"
              onClick={() => { setCancelTarget(null); setCancelReason(''); }}
              disabled={cancel.isPending}
            >
              ไม่ยกเลิก
            </button>
            <button
              className="btn-danger flex-1"
              onClick={() => void handleCancel()}
              disabled={cancel.isPending || !cancelReason.trim()}
            >
              {cancel.isPending ? 'กำลังยกเลิก…' : 'ยืนยันยกเลิกสัญญา'}
            </button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
}
